// React&NextJS
import React, { useEffect, useRef, useState } from 'react'
import router, { useRouter } from 'next/router'
// Context&Auth
import { useAuth } from '../../../context/UserContext'
import { auth } from '../../../firebase/initFirebase'
// // 3rd party libraries
// import { motion } from 'framer-motion'
// import InputMask from 'react-input-mask'
import { usePlausible } from 'next-plausible'
// Services&Helper functions
import { useKeyDown } from '../../../hooks/useKeyDown'
// Styles
import s from './Authentication.module.scss'
// Components
import { Container } from '../../core/container/Container'
import { Text } from '../../core/text/Text'
import { WhiteBox } from '../../core/whiteBox/WhiteBox'
import RafraenAudkenning from './steps/RafraenAudkenning'
import RafraenLoading from './steps/RafraenLoading'
import IslykillLoading from './steps/IslykillLoading'
import EmailComponent from './steps/Email'
import EmailLoading from './steps/EmailLoading'
import ErrorComponent from './steps/Error'

export default function Authentication({
    closeButton = null,
    authData = null,
}) {
    const { logInWithToken, user, setUser, updateUserEmail } = useAuth()
    const [params, setParams] = useState(null)
    const [phone, setPhone] = useState('')
    const [isLoaded, setIsLoaded] = useState(false)
    const [ssn, setSSN] = useState('')
    const [cToken, setCToken] = useState('')
    const [state, setState] = useState(0)
    const [isBrowser, setIsBrowser] = useState(false)
    const { query } = useRouter()
    const controllerRef = useRef<AbortController | null>()
    const plausible = usePlausible()

    useEffect(() => {
        setIsBrowser(true)
    }, [])

    useEffect(() => {
        setIsLoaded(false)
        isBrowser && setParams(new URLSearchParams(window?.location?.search))

        if (user) {
            if (!user.email) {
                setState(2)
            }
        }
        if (authData) {
            isBrowser && signinWithIslykill()
        }
        setIsLoaded(true)
        return () => {
            if (controllerRef.current) {
                controllerRef.current.abort()
                controllerRef.current = null
            }
        }
    }, [isBrowser])

    useKeyDown(['Enter'], ({ key }) => {
        if (key === 'Enter') {
            if (state === 0) {
                const btn = document.getElementById('authBtn')
                btn?.click()
            } else if (state === 2) {
                const btn = document.getElementById('emailBtn')
                btn?.click()
            }
        }
    })

    const getAuthData = async (
        url: string,
        reqBody: { [key: string]: unknown }
    ) => {
        if (controllerRef.current) {
            controllerRef.current.abort()
        }
        const controller = new AbortController()
        controllerRef.current = controller

        const res = await fetch(url, {
            method: 'POST',
            signal: controllerRef.current?.signal,
            body: JSON.stringify(reqBody),
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
        }).catch(() => {
            setState(-1)
            throw new Error('Notandi hætti við auðkenningu')
        })

        controllerRef.current = null

        return res
    }

    const validateAuthData = async (
        data: {
            customToken: string
            create: boolean
            ssn: string
            success: boolean
            message?: string
        },
        resOK: boolean
    ) => {
        if (!resOK || !data.success) {
            setState(-1)
        } else {
            const { customToken, create, ssn } = data

            setSSN(ssn)
            setCToken(customToken)

            if (!create) {
                const success = await logInWithToken(customToken)

                if (success) {
                    // If user was redirected to login because of invite to
                    // ownership on memory -> redirect back to memory after login
                    if (query?.memory) {
                        router.push(`/memories/${query.memory}`)
                    } else if (auth.currentUser?.email) {
                        // Wait for the log in to happen. Dont send the user to state 2
                    } else if (!user?.email) {
                        setState(2)
                    }
                } else {
                    console.error('Villa að skrá inn. Eitthvað fór úrskeiðis.')
                    setState(-1)
                }
            } else {
                setState(2)
            }
        }
    }

    const signUp = async () => {
        setState(1)
        try {
            // process.env.NEXT_PUBLIC_ADMIN_API_URL + 'authentication',
            const res = await getAuthData(
                process.env.NEXT_PUBLIC_ADMIN_API_URL + 'authentication',
                { phone: phone }
            )

            const dataStream = await res.text()
            const data = JSON.parse(dataStream)

            await validateAuthData(data, res.ok)
        } catch (error) {
            // If we abort the fetch request (Change page for example)
            console.log('here')

            if (error.message.includes('Notandi hætti við auðkenningu')) {
                return
            }
            console.error('Villa að skrá inn. Auðkenning rann út á tíma.')
            setState(-1)
        }
    }

    const signinWithIslykill = async () => {
        setState(4)
        try {
            await validateAuthData(authData, authData.success)
        } catch (error) {
            console.error('Villa að skrá inn. Auðkenning rann út á tíma.')
            setState(-1)
        }
    }

    if (isLoaded) {
        if (user?.email && user?.reAuth === false) {
            if (params.has('memory')) {
                const route = `/memories/${params.get('memory')}?invite=true`
                router.push(route)
            } else if (window.location.pathname === '/sign-up') {
                router.push('/account')
            } else {
                return (
                    <Container>
                        <WhiteBox className={s.container}>
                            {closeButton}
                            <div>
                                <Text variant="h2">Þú ert innskráð/ur</Text>
                            </div>
                        </WhiteBox>
                    </Container>
                )
            }
        }

        if (state === -1) {
            return (
                <ErrorComponent closeButton={closeButton} setState={setState} />
            )
        }

        if (state === 4) {
            return <IslykillLoading closeButton={closeButton} />
        }

        if (state === 3) {
            return <EmailLoading closeButton={closeButton} />
        }

        if (state === 2) {
            return (
                <EmailComponent
                    closeButton={closeButton}
                    user={user}
                    setUser={setUser}
                    auth={auth}
                    ssn={ssn}
                    cToken={cToken}
                    setState={setState}
                    updateUserEmail={updateUserEmail}
                    logInWithToken={logInWithToken}
                />
            )
        }

        if (state === 1) {
            return <RafraenLoading closeButton={closeButton} />
        }

        return (
            <RafraenAudkenning
                closeButton={closeButton}
                phone={phone}
                setPhone={setPhone}
                plausible={plausible}
                signUp={signUp}
            />
        )
    }
    return <></>
}
